"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import messages from "@/i18n/messages/en.json";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { Plus, Trash2 } from "lucide-react";
import { Button, DropdownSelect, LocationInput, PhoneInput } from "@artha/ui";
import { normalizeCountryCode } from "@artha/utils";
import {
  CandidateEditableProfileSchema,
  CANDIDATE_PROFILE_MAX_LINKS,
  CANDIDATE_PROFILE_URL_MAX_LENGTH,
} from "@artha/api-contracts";
import {
  toEditableProfile,
  toReviewForm,
  toReviewLocation,
  updateReviewLocation,
} from "../../../personalization.utils";
import { createGooglePlacesProvider } from "../../../../common/services/googlePlaces";
import {
  RESUME_NO_WORK_MODE,
  RESUME_WORK_MODES,
} from "../../../personalization.constants";

import {
  PROFILE_BASICS_FIELDS,
  PROFILE_CARD_ACTION_ICON_CLASS_NAME,
  PROFILE_CARD_EDIT_CLASS_NAME,
  PROFILE_CARD_INPUT_CLASS_NAME,
  PROFILE_CARD_LABEL_CLASS_NAME,
  PROFILE_CARD_REQUIRED_PATTERN,
  PROFILE_CARD_TITLES_ERROR,
} from "../profileCards.constants";
import type {
  ProfileBasicsField,
  ProfileBasicsSectionProps,
} from "../profileCards.types";

export function ProfileBasicsEditor({
  profile,
  onSave,
  onCancel,
  editingContact,
}: Pick<
  ProfileBasicsSectionProps,
  "profile" | "onSave" | "onCancel" | "editingContact"
>) {
  const [form, setForm] = useState(() => toReviewForm(profile));
  const [error, setError] = useState("");
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const t = useTranslations("profile.resumePersonalization");
  const id = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const locationProvider = useMemo(() => createGooglePlacesProvider(), []);
  useEffect(() => {
    if (!editingContact) return;
    const input = formRef.current?.querySelector<HTMLInputElement>(
      `[id="${id}-${editingContact}"]`,
    );
    input?.focus();
    input?.scrollIntoView({ block: "center" });
  }, [editingContact, id]);
  return (
    <form
      ref={formRef}
      aria-label={messages.talentNetworkUi.edit_profile}
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (isResolvingLocation) return;
        const next = toEditableProfile(form);
        const parsed = CandidateEditableProfileSchema.safeParse(next);
        if (!parsed.success) {
          setError(
            parsed.error.issues.some((issue) => issue.path[0] === "titles")
              ? PROFILE_CARD_TITLES_ERROR
              : parsed.error.issues[0].message,
          );
          return;
        }
        onSave?.(parsed.data);
      }}
    >
      {PROFILE_BASICS_FIELDS.map((field: ProfileBasicsField) => (
        <div key={field.key}>
          <label
            htmlFor={`${id}-${field.key}`}
            className={PROFILE_CARD_LABEL_CLASS_NAME}
          >
            {field.label}
            {field.required ? " *" : ""}
          </label>
          <input
            id={`${id}-${field.key}`}
            aria-label={field.label}
            type={field.type}
            required={field.required}
            pattern={
              field.required && field.type === "text"
                ? PROFILE_CARD_REQUIRED_PATTERN
                : undefined
            }
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            min={field.type === "number" ? 0 : undefined}
            max={field.type === "number" ? 80 : undefined}
            step={field.type === "number" ? "any" : undefined}
            className={PROFILE_CARD_INPUT_CLASS_NAME}
            value={form[field.key]}
            onChange={(event) =>
              setForm({ ...form, [field.key]: event.target.value })
            }
          />
        </div>
      ))}
      <LocationInput
        value={toReviewLocation(form)}
        onChange={(location) =>
          setForm((current) => updateReviewLocation(current, location))
        }
        provider={locationProvider}
        onResolvingChange={setIsResolvingLocation}
        labelClassName={PROFILE_CARD_LABEL_CLASS_NAME}
        controlClassName={PROFILE_CARD_INPUT_CLASS_NAME}
      />
      <PhoneInput
        id={`${id}-phone`}
        value={form.phone}
        defaultCountry={normalizeCountryCode(form.country)}
        label={messages.talentNetworkUi.phone_number_2}
        countryLabel={t("review.phoneCountry")}
        searchPlaceholder={t("review.phoneCountrySearch")}
        emptyLabel={t("review.phoneCountryEmpty")}
        description={messages.common.phoneOtpHint}
        labelClassName={PROFILE_CARD_LABEL_CLASS_NAME}
        controlClassName={PROFILE_CARD_INPUT_CLASS_NAME}
        onChange={(phone) => setForm({ ...form, phone })}
      />
      <div>
        <label
          className={PROFILE_CARD_LABEL_CLASS_NAME}
          htmlFor={`${id}-workMode`}
        >
          {messages.talentNetworkUi.preferred_work_mode}
        </label>
        <DropdownSelect
          id={`${id}-workMode`}
          ariaLabel={talentNetworkText("preferred_work_mode")}
          value={form.workMode || RESUME_NO_WORK_MODE}
          options={[
            ...RESUME_WORK_MODES.map((value) => ({
              value,
              label: t(`review.workModeOptions.${value}`),
            })),
            {
              value: RESUME_NO_WORK_MODE,
              label: t("review.workModeOptions.noPreference"),
            },
          ]}
          className={PROFILE_CARD_INPUT_CLASS_NAME}
          onValueChange={(value) =>
            setForm({
              ...form,
              workMode: value === RESUME_NO_WORK_MODE ? "" : value,
            })
          }
        />
      </div>
      <fieldset className="space-y-3">
        <legend className={PROFILE_CARD_LABEL_CLASS_NAME}>
          {messages.talentNetworkUi.profile_links}
        </legend>
        {(form.links || []).map((link, index) => (
          <div key={index} className="space-y-2">
            <label
              htmlFor={`${id}-link-label-${index}`}
              className={PROFILE_CARD_LABEL_CLASS_NAME}
            >
              {messages.talentNetworkUi.link}
              {index + 1}
              {messages.talentNetworkUi.label}
            </label>
            <input
              id={`${id}-link-label-${index}`}
              value={link.label || ""}
              placeholder={messages.talentNetworkUi.linkedin_github_portfolio}
              maxLength={80}
              className={PROFILE_CARD_INPUT_CLASS_NAME}
              onChange={(event) =>
                setForm({
                  ...form,
                  links: form.links?.map((item, position) =>
                    position === index
                      ? { ...item, label: event.target.value }
                      : item,
                  ),
                })
              }
            />
            <label
              htmlFor={`${id}-link-url-${index}`}
              className={PROFILE_CARD_LABEL_CLASS_NAME}
            >
              {messages.talentNetworkUi.link}
              {index + 1}
              {messages.talentNetworkUi.url}
            </label>
            <div className="flex items-center gap-2">
              <input
                id={`${id}-link-url-${index}`}
                type="url"
                required
                pattern="https?://.*"
                value={link.url}
                placeholder={"https://"}
                maxLength={CANDIDATE_PROFILE_URL_MAX_LENGTH}
                className={PROFILE_CARD_INPUT_CLASS_NAME}
                onChange={(event) =>
                  setForm({
                    ...form,
                    links: form.links?.map((item, position) =>
                      position === index
                        ? { ...item, url: event.target.value }
                        : item,
                    ),
                  })
                }
              />
              <button
                type="button"
                aria-label={talentNetworkText("remove_link", {
                  number: index + 1,
                })}
                className={PROFILE_CARD_EDIT_CLASS_NAME}
                onClick={() =>
                  setForm({
                    ...form,
                    links: form.links?.filter(
                      (_, position) => position !== index,
                    ),
                  })
                }
              >
                <Trash2
                  aria-hidden="true"
                  className={PROFILE_CARD_ACTION_ICON_CLASS_NAME}
                />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0D41E1] disabled:opacity-40"
          disabled={(form.links?.length || 0) >= CANDIDATE_PROFILE_MAX_LINKS}
          onClick={() =>
            setForm({ ...form, links: [...(form.links || []), { url: "" }] })
          }
        >
          <Plus
            aria-hidden="true"
            className={PROFILE_CARD_ACTION_ICON_CLASS_NAME}
          />
          {messages.talentNetworkUi.add_link}
        </button>
      </fieldset>
      {error ? (
        <p role="alert" className="text-xs text-[#B42318]">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2 pt-2">
        <Button
          type="submit"
          disabled={isResolvingLocation}
          className="h-9 rounded-lg text-sm"
        >
          {messages.talentNetworkUi.save_profile}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9 rounded-lg text-sm"
          onClick={onCancel}
        >
          {messages.talentNetworkUi.cancel}
        </Button>
      </div>
    </form>
  );
}
